import React, { createContext, useContext, useEffect, useState } from 'react';
import { IDIOMA_POR_DEFECTO, esIdiomaSoportado, fijarIdiomaActivo, type Idioma } from '@/i18n';

type Theme = 'light' | 'dark' | 'system';
type Language = Idioma;
type RefreshInterval = 5000 | 15000 | 30000 | 60000;

interface Settings {
    theme: Theme;
    language: Language;
    refreshInterval: RefreshInterval;
}

interface SettingsContextValue extends Settings {
    resolvedTheme: 'light' | 'dark';
    setTheme: (theme: Theme) => void;
    setLanguage: (language: Language) => void;
    setRefreshInterval: (interval: RefreshInterval) => void;
}

const STORAGE_KEY = 'metrosafe.settings';

const defaults: Settings = {
    theme: 'system',
    language: IDIOMA_POR_DEFECTO,
    refreshInterval: 15000,
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

function loadSettings(): Settings {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return defaults;
        const parsed = JSON.parse(raw);
        const settings: Settings = { ...defaults, ...parsed };
        // Los ajustes guardados pueden venir de cuando solo existian 'es' y 'en', o
        // traer un idioma que ya no se sirve: cualquier valor desconocido vuelve al
        // idioma por defecto en vez de dejar el panel sin traducciones.
        if (!esIdiomaSoportado(settings.language)) settings.language = IDIOMA_POR_DEFECTO;
        return settings;
    } catch {
        return defaults;
    }
}

function systemPrefersDark(): boolean {
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

function applyTheme(theme: Theme): 'light' | 'dark' {
    const resolved = theme === 'system' ? (systemPrefersDark() ? 'dark' : 'light') : theme;
    const root = document.documentElement;
    if (resolved === 'dark') {
        root.classList.add('dark');
    } else {
        root.classList.remove('dark');
    }
    return resolved;
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
    const [settings, setSettings] = useState<Settings>(() => {
        const inicial = loadSettings();
        // Antes del primer render: el codigo que no es componente (helpers de fecha,
        // mensajes de lib/) traduce con el idioma activo de modulo.
        fijarIdiomaActivo(inicial.language);
        return inicial;
    });
    const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>(() => applyTheme(loadSettings().theme));

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
        setResolvedTheme(applyTheme(settings.theme));
        fijarIdiomaActivo(settings.language);
        document.documentElement.lang = settings.language;
    }, [settings]);

    useEffect(() => {
        if (settings.theme !== 'system') return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = () => setResolvedTheme(applyTheme('system'));
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [settings.theme]);

    const value: SettingsContextValue = {
        ...settings,
        resolvedTheme,
        setTheme: (theme) => setSettings((s) => ({ ...s, theme })),
        setLanguage: (language) => setSettings((s) => ({ ...s, language })),
        setRefreshInterval: (refreshInterval) => setSettings((s) => ({ ...s, refreshInterval })),
    };

    return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
    const ctx = useContext(SettingsContext);
    if (!ctx) throw new Error('useSettings must be used inside SettingsProvider');
    return ctx;
}

/**
 * Variante que devuelve `null` en vez de lanzar cuando no hay provider por encima.
 * La usa `useT`: los componentes compartidos (`table-state`, `query-error`) se montan
 * aislados en las pruebas, y un texto traducido no debe obligar a envolverlos en todo
 * el árbol de contextos de la aplicación.
 */
export function useSettingsOpcional() {
    return useContext(SettingsContext);
}
